import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, map, Observable } from 'rxjs';

import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { State, StateService } from '@app/services/state/state.service';
import { PortalFeatures, PortalLeadNode, PortalOrderNode } from '@app/services/api/api.types';
import { Steps } from '@app/components/stepper/stepper.component';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalRoutes } from '@app/app-routing.module';
import { isTiersFeature } from '@app/pages/tiers-page/utils';
import { PriceSummaryAction } from '@voyagu/portal-design-system';
import { MemoizedValue } from '@app/utils/memoized-value.util';

@Component({
  selector: 'gratitude-page',
  templateUrl: './gratitude-page.component.html',
  styleUrls: ['./gratitude-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GratitudePageComponent implements OnInit {
  Steps = Steps;

  portalLeadService = inject(PortalLeadService);
  portalOrderService = inject(PortalOrderService);
  stateService = inject(StateService);
  location = inject(Location);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  portalLead$: Observable<PortalLeadNode | null>;
  portalOrder: PortalOrderNode;
  state: State;
  customValue: string | null = null;
  isCustomValueError: boolean;

  portalTiers: boolean;
  ancillaryUpsell: boolean;

  tipsAmounts: string[] = [];
  nextButtonTitle = 'Continue Booking';
  noTipsValue = '0';

  minimalPrefilledTipsValue = 100;
  prefilledValuesCount = 8;

  tipBrackets = [
    { min: 2000, max: 2999, tip: 150 },
    { min: 3000, max: 3999, tip: 200 },
    { min: 4000, max: 4999, tip: 250 },
    { min: 5000, max: 5999, tip: 300 },
    { min: 6000, max: 6999, tip: 350 },
    { min: 7000, max: 7999, tip: 400 },
    { min: 8000, max: 8999, tip: 450 },
    { min: 9000, max: 9999, tip: 500 },
    { min: 10000, max: 14999, tip: 550 },
    { min: 15000, max: 19999, tip: 600 },
    { min: 20000, max: Infinity, tip: 650 },
  ];

  ngOnInit() {
    this.ancillaryUpsell = this.activatedRoute.snapshot.data['ancillaryUpsell'] as boolean;

    this.state = this.stateService.getState();
    this.getPortalLead();
  }

  generateTipsAmount(initialValue: number) {
    for (let i = 0; i < this.prefilledValuesCount; i++) {
      this.tipsAmounts.push((initialValue + i * 50).toString());
    }
  }

  getInitialPrefilledTip(totalPrice: number): number {
    for (const bracket of this.tipBrackets) {
      if (totalPrice >= bracket.min && totalPrice <= bracket.max) {
        return bracket.tip;
      }
    }

    return this.minimalPrefilledTipsValue;
  }

  getPortalLead() {
    this.portalLead$ = combineLatest([
      this.portalLeadService.portalLead$,
      this.portalOrderService.portalOrder$,
    ]).pipe(
      map(([portalLead, portalOrder]) => {
        if (portalLead) {
          this.portalTiers =
            portalLead.salesAgent?.portalFeatures?.includes(PortalFeatures.portal_tiers) &&
            isTiersFeature(portalOrder);
        }

        if (portalOrder) {
          const total = this.calculateTotal(portalOrder);
          const initialValue = this.getInitialPrefilledTip(total);
          this.generateTipsAmount(initialValue);

          if (!this.state.tipsAmount) {
            this.updateState(this.tipsAmounts[0]);
          }

          this.portalOrder = portalOrder;
        }

        const tipsAmount = this.state.tipsAmount?.toString();

        if (tipsAmount && !this.isCustomValueExisted(tipsAmount)) {
          this.customValue = tipsAmount.toString();
        }

        Tracking.track(PortalTrackingEvents.portalCheckoutGratitudeOpened);

        return portalLead;
      }),
    );
  }

  back() {
    this.location.back();

    Tracking.track(PortalTrackingEvents.portalCheckoutGratitudeBackPressed);
  }

  summaryActionsCache = new MemoizedValue<PriceSummaryAction[]>();

  get summaryActions(): PriceSummaryAction[] {
    const { nextButtonTitle, isCustomValueError } = this;

    return this.summaryActionsCache.get(`${nextButtonTitle}|${isCustomValueError}`, () => [
      {
        id: 'continue',
        label: nextButtonTitle,
        variant: 'primary',
        disabled: !!isCustomValueError,
      },
      { id: 'back', label: 'Back', variant: 'secondary' },
    ]);
  }

  onSummaryAction(action: PriceSummaryAction) {
    if (action.id === 'back') {
      this.back();
      return;
    }

    if (this.isCustomValueError) return;

    this.submit();
  }

  selectTipsAmount(tipsAmount: string) {
    if (tipsAmount) {
      this.isCustomValueError = false;
    }

    this.updateState(tipsAmount);
  }

  updateState(tipsAmount: string) {
    this.stateService.patchState({ tipsAmount: tipsAmount ? +tipsAmount : undefined });
    this.state = this.stateService.getState();

    Tracking.track(PortalTrackingEvents.portalCheckoutGratitudeEntered, {
      gratitude: tipsAmount ? tipsAmount : 'no',
    });
  }

  submit() {
    const path = this.ancillaryUpsell
      ? [
          PortalRoutes.AncillaryUpsell,
          this.activatedRoute.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam),
          PortalRoutes.OverviewPayment,
        ]
      : [
          `${this.state.portalLink}`,
          `${this.portalOrder.portalLink}`,
          PortalRoutes.OverviewPayment,
        ];

    void this.router.navigate(path, { queryParamsHandling: 'merge' });

    Tracking.track(PortalTrackingEvents.portalCheckoutGratitudeContinuePressed);
  }

  onCustomValueFocusOut(value: string) {
    this.isCustomValueError = !value;

    if (this.isCustomValueExisted(value)) {
      this.customValue = '';
    } else {
      this.customValue = value;
    }

    this.selectTipsAmount(value);
  }

  updateCustomValue(value: string) {
    this.customValue = value;

    if (this.customValue) {
      this.isCustomValueError = false;
    }

    this.updateState(value);
  }

  isCustomValueExisted(tipsAmount: string): boolean {
    if (tipsAmount === '0') {
      return true;
    }

    return this.tipsAmounts.some((item) => item === tipsAmount);
  }

  calculateTotal(portalOrder: PortalOrderNode): number {
    return (
      (portalOrder?.flightsTotal || 0) +
      (this.state.disruptionProtection || 0) +
      (this.state.cancelForAnyReason || 0) +
      (this.state.bundle?.price || 0) +
      (this.state.promocodeDiscountAmount || 0)
    );
  }
}
